import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import axios, { type CancelTokenSource } from 'axios'
import { debounce } from 'lodash'
import { type IDropdownItem, toastStore, Avatar, Icon } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import {
  type IResponseCannedMessage,
  SavedRepliesSortEnum,
  type IParamsGetCannedMessages,
} from 'entities/CannedMessages'
import { CannedMessage } from 'entities/CannedMessages/model/CannedMessage'
import { CannedMessagesApi } from 'entities/CannedMessages/api/savedReplies'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey } from 'entities/Integrations'
import { SavedRepliesPath } from 'pages/settings/pages/savedReplies/route/savedRepliesPath'
import { NewSavedReplyStore } from './NewSavedReplyStore'
import { type ISavedRepliesStoreConfig } from './types'
import { ListSavedRepliesStore } from './ListSavedRepliesStore'

export class SavedRepliesStore {
  private _disposeReactionLoadData: IReactionDisposer | null = null
  private _disposeReactionCanManageSavedReplies: IReactionDisposer | null = null
  private _savedRepliesMap = new Map<number, CannedMessage>()
  private _selectedSavedReply: CannedMessage | null = null
  private _selectedSavedReplyRef: React.RefObject<HTMLButtonElement> | null = null
  private _useScrollToElement = false
  private _search = ''
  private _loading = false
  private _saveModalId = ''
  private _visibleSavedRepliesActionsId = 0
  private _leftContentElement: HTMLDivElement | null = null
  private _cancelTokenSource: CancelTokenSource | null = null
  private readonly _newSavedReplyStore: NewSavedReplyStore
  private readonly _favoriteListStore: ListSavedRepliesStore
  private readonly _unfavoriteListStore: ListSavedRepliesStore
  private _showCreateContent = false

  private _debounceSearch: ReturnType<typeof debounce>

  constructor(private _entities: IEntitiesStore, config?: ISavedRepliesStoreConfig) {
    makeAutoObservable(this)

    this._newSavedReplyStore = new NewSavedReplyStore(
      {
        savedRepliesStore: this,
        mergeFieldProps: config?.mergeFieldProps,
      },
      this._entities
    )
    this._favoriteListStore = new ListSavedRepliesStore({
      filter: 'favorite',
      setSavedReplay: this.setSavedReplay,
    })
    this._unfavoriteListStore = new ListSavedRepliesStore({
      filter: 'unfavorite',
      setSavedReplay: this.setSavedReplay,
    })

    this._debounceSearch = debounce(this.loadData, 500)
    this.reactionLoadData()
    this.reactionCanManageSavedReplies()
  }

  get showCreateContent() {
    return this._showCreateContent
  }

  get leftContentElement() {
    return this._leftContentElement
  }

  get visibleSavedRepliesActionsId() {
    return this._visibleSavedRepliesActionsId
  }

  get isEmpty() {
    return (
      this._savedRepliesMap.size === 0 &&
      this._favoriteListStore.total === 0 &&
      this._unfavoriteListStore.total === 0
    )
  }

  get selectedSavedReply() {
    return this._selectedSavedReply
  }

  get useScrollToElement() {
    return this._useScrollToElement
  }

  get selectedSavedReplyRef() {
    return this._selectedSavedReplyRef
  }

  get search() {
    return this._search
  }

  get entitiesStore() {
    return this._entities
  }

  get unfavoriteListStore() {
    return this._unfavoriteListStore
  }

  get favoriteListStore() {
    return this._favoriteListStore
  }

  get newSavedReplyStore() {
    return this._newSavedReplyStore
  }

  setShowCreateContent = (showCreateContent: boolean) => {
    this._showCreateContent = showCreateContent
  }

  setSavedReplay = (savedReplayResponse: IResponseCannedMessage) => {
    const savedReplay = new CannedMessage(savedReplayResponse, this._entities)
    if (this._selectedSavedReply?.id === savedReplay.id) {
      this._selectedSavedReply = savedReplay
    }
    this._savedRepliesMap.set(savedReplay.id, savedReplay)
  }

  setSelectedSavedReplyRef = (ref: React.RefObject<HTMLButtonElement> | null) => {
    this._selectedSavedReplyRef = ref
  }

  setUseScrollToElement = (useScroll: boolean) => {
    this._useScrollToElement = useScroll
  }

  get showEmptyContent() {
    return !this.loading && this.isEmpty && !this.showCreateContent && !this.search
  }

  get order(): SavedRepliesSortEnum {
    return this._entities.cannesMessageStore.order
  }

  get paramsGetItems(): IParamsGetCannedMessages {
    return {
      order: this.order,
      search: this._search,
    }
  }

  get loading() {
    return this._favoriteListStore.loading || this._unfavoriteListStore.loading || this._loading
  }

  get savedReplies() {
    const list = Array.from(this._savedRepliesMap.values())

    if (this.order === SavedRepliesSortEnum.letterDown) {
      list.sort((a, b) => {
        if (a.title.toLowerCase() < b.title.toLowerCase()) return -1
        if (a.title.toLowerCase() > b.title.toLowerCase()) return 1
        return 0
      })
    }

    if (this.order === SavedRepliesSortEnum.letterUp) {
      list.sort((a, b) => {
        if (a.title.toLowerCase() > b.title.toLowerCase()) return -1
        if (a.title.toLowerCase() < b.title.toLowerCase()) return 1
        return 0
      })
    }

    if (this.order === SavedRepliesSortEnum.newest) {
      list.sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp)
    }

    if (this.order === SavedRepliesSortEnum.oldest) {
      list.sort((a, b) => a.createdAtTimestamp - b.createdAtTimestamp)
    }

    return list
  }

  get hasFavorites() {
    return !!this.favoriteSavedReplies.length
  }

  get hasUnfavorites() {
    return !!this.unfavoriteSavedReplies.length
  }

  get hasSavedReplies() {
    return this.hasFavorites || this.hasUnfavorites
  }

  get favoriteSavedReplies() {
    return this.savedReplies.filter((reply) => reply.is_favorite)
  }

  get unfavoriteSavedReplies() {
    return this.savedReplies.filter((reply) => !reply.is_favorite)
  }

  get canManageSavedReplies() {
    if (this._entities.usersStore.user?.isOwnerRole) return true

    return this._entities.userPermissionsStore.getItem('can_manage_canned_messages')
  }

  get savedRepliesPermissionError() {
    return this.canManageSavedReplies ? null : (
      <>
        Contact your admin to enable
        <br />
        saved reply management
      </>
    )
  }

  get isMySavedReply() {
    return this.selectedSavedReply?.owner_id === this._entities.usersStore.user?.id
  }

  get savedReplyOwner() {
    return this._entities.usersStore.getItem(this.selectedSavedReply?.owner_id)
  }

  get savedReplyVisibilityIcon() {
    const avatarInfo = this._entities.usersStore.user?.avatarInfo

    if (!this.newSavedReplyStore.isEdit && avatarInfo) return <Avatar info={avatarInfo} size={16} />

    return this.savedReplyOwner ? (
      <Avatar info={this.savedReplyOwner?.avatarInfo} size={16} />
    ) : (
      <Icon icon='userLine' />
    )
  }

  get savedReplyVisibilityText() {
    if (!this.newSavedReplyStore.isEdit || this.isMySavedReply) return 'Myself only'

    return this.savedReplyOwner ? `${this.savedReplyOwner.full_name} only` : 'Deleted user only'
  }

  get canManageAllSavedReplies() {
    return !!(
      (this.canManageSavedReplies && this._entities.usersStore.user?.isAdminRole) ||
      this._entities.usersStore.user?.isOwnerRole
    )
  }

  nextItem = () => {
    this._useScrollToElement = true
    const fullList = [...this.favoriteSavedReplies, ...this.unfavoriteSavedReplies]

    if (!this._selectedSavedReply && fullList.length) this._selectedSavedReply = fullList[0]
    if (!this._selectedSavedReply || !fullList.length || fullList.length === 1) return

    const currentIdx = fullList.findIndex((item) => item.id === this._selectedSavedReply?.id)

    if (fullList.length > currentIdx + 1) {
      const item = fullList[currentIdx + 1]
      if (item) this._selectedSavedReply = item
    } else if (fullList.length) {
      this._selectedSavedReply = fullList[0]
    }
  }

  prevItem = () => {
    this._useScrollToElement = true
    const fullList = [...this.favoriteSavedReplies, ...this.unfavoriteSavedReplies]

    if (!this._selectedSavedReply && fullList.length) this._selectedSavedReply = fullList[0]
    if (!this._selectedSavedReply || !fullList.length || fullList.length === 1) return

    const currentIdx = fullList.findIndex((item) => item.id === this._selectedSavedReply?.id)

    if (currentIdx > 0) {
      const item = fullList[currentIdx - 1]
      if (item) this._selectedSavedReply = item
    } else {
      this._selectedSavedReply = fullList[fullList.length - 1]
    }
  }

  setFirstSavedReply = () => {
    const firstSavedReplay = this.favoriteSavedReplies[0] || this.unfavoriteSavedReplies[0]
    this._selectedSavedReply = this.savedReplies.length ? firstSavedReplay : null
    this.handleEditSavedReplies(this._selectedSavedReply)
  }

  loadData = async ({ selectedSavedReply }: { selectedSavedReply?: CannedMessage } = {}) => {
    try {
      runInAction(() => {
        this._loading = true
      })
      this.clear()

      await Promise.all([
        this._favoriteListStore.loadData(this.paramsGetItems),
        this._unfavoriteListStore.loadData(this.paramsGetItems),
      ])

      runInAction(() => {
        if (!selectedSavedReply) {
          this.setFirstSavedReply()
        } else {
          this._selectedSavedReply = selectedSavedReply
          this.handleEditSavedReplies(selectedSavedReply)
        }
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  reset = () => {
    this._disposeReactionLoadData?.()
    this._disposeReactionCanManageSavedReplies?.()
    this._search = ''
    this.clear()
    this._favoriteListStore.reset()
    this._unfavoriteListStore.reset()
  }

  clear = () => {
    this._savedRepliesMap.clear()
    this._favoriteListStore.clear()
    this._unfavoriteListStore.clear()
    this._selectedSavedReply = null
    this._newSavedReplyStore.reset()
  }

  initCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  init = async () => {
    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.integrationsStore.fetchIntegrations(),
    ])

    const salesforceIntegration = this._entities.integrationsStore.getIntegration(
      IntegrationKey.salesforce
    )
    if (salesforceIntegration?.isConnected) {
      await this._entities.mergeFieldStore.updateIntegrationFields(IntegrationKey.salesforce)
    }

    await this._entities.mergeFieldStore.loadAllFields()
    this.reset()
    this.reactionLoadData()
    this.reactionCanManageSavedReplies()
    await this.loadData()
  }

  handleSelectSort = (item: IDropdownItem) => {
    this._entities.cannesMessageStore.setOrder(item.id as SavedRepliesSortEnum)
  }
  setSearch = (search: string) => {
    this._search = search
  }

  clearSearch = () => {
    this._search = ''
  }

  setSelectSavedReply = (savedReply: CannedMessage | null) => {
    this._selectedSavedReply = savedReply
  }

  handleEditSavedReplies = (savedReply: CannedMessage | null) => {
    this._newSavedReplyStore.handleNewSavedReply(savedReply)
  }

  handleDuplicateSavedReplies = (savedReply: CannedMessage | null) => {
    uiStore.changeRoute({
      path: SavedRepliesPath.create,
    })
    this.setSelectSavedReply(null)
    this._newSavedReplyStore.handleNewSavedReply(savedReply, true)
  }

  handleChangeVisibilitySavedReplies = async (
    savedReply: CannedMessage | null,
    isPublic: boolean
  ) => {
    try {
      if (savedReply) {
        const { data } = await CannedMessagesApi.updateCannedMessagesById({
          ...savedReply.updateData,
          public: isPublic,
        })
        if (data) {
          this.setSavedReplay(data)
          this._newSavedReplyStore.setPublic(data.public)
        }
        const id = nanoid()
        const onUndo = () => {
          toastStore.remove(id)
          this.handleChangeVisibilitySavedReplies(savedReply, !isPublic)
        }

        const isMySavedReply = this._entities.usersStore.user?.id === savedReply.owner_id
        const savedReplyOwner = this._entities.usersStore.getItem(savedReply.owner_id)

        toastStore.add({
          id: id,
          title: `Now visible ${
            data.public
              ? 'to all members'
              : !isMySavedReply
              ? `only to ${savedReplyOwner ? savedReplyOwner.full_name : 'Deleted user'}`
              : 'only to you'
          }`,
          type: 'info',
          action: {
            text: 'Undo',
            onAction: onUndo,
          },
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  handleLeaveWithoutSaving = (onAction: () => void) => {
    if (modalStore.hasModal(this._saveModalId)) return
    this._saveModalId = nanoid()

    modalStore.addModal({
      id: this._saveModalId,
      type: ModalTypeList.INFO,
      title: 'Leave without saving?',
      primaryAction: {
        text: 'Yes, leave',
        onAction: () => {
          modalStore.closeModal(this._saveModalId)
          onAction()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(this._saveModalId)
        },
      },
    })
  }

  handleSelectSavedReply = (savedReply: CannedMessage | null, isChangeRoute = false) => {
    if (this.newSavedReplyStore.hasChanges) {
      this.handleLeaveWithoutSaving(() => {
        this.setSelectSavedReply(savedReply)
        this.handleEditSavedReplies(savedReply)

        if (isChangeRoute) {
          uiStore.changeRoute({
            path: SavedRepliesPath.root,
          })
        }
      })

      return
    }

    this.setSelectSavedReply(savedReply)
    this.handleEditSavedReplies(savedReply)

    if (isChangeRoute) {
      uiStore.changeRoute({
        path: SavedRepliesPath.root,
      })
    }
  }

  onDeleteSavedReply = async (savedReply: CannedMessage) => {
    try {
      const index = this.savedReplies.findIndex((reply) => savedReply.id === reply.id)

      this._savedRepliesMap.delete(savedReply.id)
      if (this._selectedSavedReply?.id === savedReply.id) {
        const nextItem = this.savedReplies[index] || null

        if (nextItem) {
          this._selectedSavedReply = nextItem
          this.handleEditSavedReplies(nextItem)
        } else {
          this.setFirstSavedReply()
        }
      }

      await CannedMessagesApi.deleteCannedMessages(savedReply.id)
    } catch (e) {
      logger.error(e)
    }
  }

  handleDeleteSavedReplies = async (savedReply: CannedMessage | null) => {
    try {
      if (savedReply) {
        const nanoId = nanoid()

        const deleteTimeoutId = setTimeout(async () => {
          toastStore.remove(nanoId)
          this.onDeleteSavedReply(savedReply)
        }, 5000)

        toastStore.add({
          id: nanoId,
          title: 'Saved reply deleted',
          type: 'info',
          action: {
            text: 'Undo',
            onAction: () => {
              clearTimeout(deleteTimeoutId)
              toastStore.remove(nanoId)
            },
          },
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  handleFavoriteSavedReplies = async (savedReply: CannedMessage) => {
    try {
      const { data } = await CannedMessagesApi.favoriteCannedMessagesById({
        id: savedReply.id,
        favorite: !savedReply.is_favorite,
      })
      if (data) {
        this.setSavedReplay(data)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setVisibleSavedRepliesActionsId = (id: number) => {
    this._visibleSavedRepliesActionsId = id
  }

  setLeftContentElement = (leftContentElement: HTMLDivElement) => {
    this._leftContentElement = leftContentElement
  }

  handleReactionLoadData = () => {
    runInAction(() => {
      this._loading = true
    })
    this._favoriteListStore.cancelRequest.reset()
    this._unfavoriteListStore.cancelRequest.reset()
    this._debounceSearch()
  }

  reactionLoadData = () => {
    this._disposeReactionLoadData?.()
    this._disposeReactionLoadData = reaction(
      () => this.paramsGetItems,
      () => {
        if (this.newSavedReplyStore.hasChanges) {
          this.handleLeaveWithoutSaving(() => {
            this.handleReactionLoadData()
          })
          return
        }

        this.handleReactionLoadData()
      }
    )
  }

  reactionCanManageSavedReplies = () => {
    this._disposeReactionCanManageSavedReplies?.()
    this._disposeReactionCanManageSavedReplies = reaction(
      () => this.canManageSavedReplies,
      () => {
        this.handleReactionLoadData()
      }
    )
  }
}
